import { useCallback, useEffect, useRef, useState } from 'react';

import type { PetSnapshot } from '../../pets/types';
import { api, logHttpError } from '../../services/api';

type PreferenceUpdate =
  | { enabled: boolean }
  | { notifications_enabled: boolean };

const PetSettings = () => {
  const [snapshot, setSnapshot] = useState<PetSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const loadSnapshot = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await api.get<PetSnapshot>('/pets/me');
      if (mountedRef.current) setSnapshot(response.data);
    } catch (error: unknown) {
      logHttpError('Ошибка загрузки настроек PET-системы', error);
      if (mountedRef.current) setLoadError('Не удалось загрузить настройки питомца.');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void loadSnapshot();
    return () => { mountedRef.current = false; };
  }, [loadSnapshot]);

  const savePreference = async (update: PreferenceUpdate) => {
    if (!snapshot || saving) return;

    const previous = snapshot;
    const optimistic = { ...previous, ...update };
    setSnapshot(optimistic);
    setSaving(true);
    setSaveError(null);

    try {
      const response = !previous.active && 'enabled' in update && update.enabled
        ? await api.post<PetSnapshot>('/pets/enable')
        : await api.put<PetSnapshot>('/pets/preferences', update);
      if (mountedRef.current) setSnapshot(response.data);
    } catch (error: unknown) {
      logHttpError('Ошибка сохранения настроек PET-системы', error);
      if (mountedRef.current) {
        setSnapshot(previous);
        setSaveError('Не удалось сохранить настройки питомца.');
      }
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  if (loading && snapshot === null) {
    return <p role="status" className="shark-muted py-4 text-sm">Загрузка настроек питомца...</p>;
  }

  if (snapshot === null) {
    return (
      <section aria-label="PET-система" className="shark-card shark-border rounded-2xl border">
        <p role="alert" className="shark-error text-sm">{loadError ?? 'Не удалось загрузить настройки питомца.'}</p>
        <button type="button" onClick={() => void loadSnapshot()} className="shark-accent-button shark-density-control mt-4 rounded-xl text-sm font-bold">
          Повторить
        </button>
      </section>
    );
  }

  const enabled = snapshot.enabled;

  return (
    <section aria-label="PET-система" className="shark-density-stack">
      <article className="shark-card shark-border rounded-2xl border">
        <h4 className="text-lg font-bold text-white">PET-система</h4>
        <p className="shark-muted mt-1 text-sm">Управляйте питомцем и уведомлениями о его состоянии.</p>

        <div className="mt-5 space-y-5">
          <label className="flex cursor-pointer items-center justify-between gap-4">
            <span>
              <span className="block text-sm font-bold text-white">Показывать PET-систему</span>
              <span className="shark-muted mt-0.5 block text-xs">Показывать питомца в ленте и разрешить уход за ним.</span>
            </span>
            <input
              type="checkbox"
              aria-label="Показывать PET-систему"
              checked={enabled}
              disabled={saving}
              onChange={(event) => void savePreference({ enabled: event.target.checked })}
              className="h-5 w-5 shrink-0 accent-blue-600"
            />
          </label>

          <label className={`flex items-center justify-between gap-4 ${enabled && !saving ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
            <span>
              <span className="block text-sm font-bold text-white">PET-уведомления</span>
              <span className="shark-muted mt-0.5 block text-xs">Напоминать, когда питомцу нужны внимание и забота.</span>
            </span>
            <input
              type="checkbox"
              aria-label="PET-уведомления"
              checked={snapshot.notifications_enabled}
              disabled={!enabled || saving}
              onChange={(event) => void savePreference({ notifications_enabled: event.target.checked })}
              className="h-5 w-5 shrink-0 accent-blue-600"
            />
          </label>
        </div>

        <div className="mt-5 rounded-xl border border-gray-700 bg-gray-900/40 p-3">
          <p className="shark-muted text-sm">Прогресс питомца заморожен при отключении. Питомец и накопленный прогресс не удаляются.</p>
        </div>

        {saving && <p role="status" className="shark-muted mt-3 text-sm">Сохранение...</p>}
        {saveError && <p role="alert" className="shark-error mt-3 text-sm">{saveError}</p>}
      </article>
    </section>
  );
};

export default PetSettings;
